import type { OAuthConsentClientResponse } from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./oauth-consent-client-card.i18n";

type Props = {
  client: OAuthConsentClientResponse;
};

/**
 * Who is asking for access on the OAuth consent page. The name and website are
 * self-declared by the client, so the redirect host — where the sign-in is
 * actually sent — is shown next to them, with a warning when we do not know it.
 */
export function OAuthConsentClientCard({ client }: Props) {
  const t = useT(T);
  const trustNote =
    client.redirectTrust === "loopback"
      ? t.redirectLoopback
      : client.redirectTrust === "known"
        ? t.redirectKnown
        : client.redirectTrust === "first-party"
          ? t.redirectFirstParty
          : null;

  return (
    <div className="mb-6 space-y-3">
      <div className="p-4 bg-muted rounded-lg text-center">
        <p className="text-2xl font-semibold break-words">{client.name ?? t.unnamedApp}</p>
        {/* Plain text on purpose: the URI is unverified, so it is not a link. */}
        {client.uri && <p className="text-sm text-muted-foreground break-all mt-1">{client.uri}</p>}
        <p className="text-sm text-muted-foreground mt-2">{t.appWantsAccess}</p>
        {/* A first-party client's name is ours, defined in code. */}
        {client.redirectTrust !== "first-party" && (
          <p className="text-xs text-muted-foreground mt-1">{t.selfDeclared}</p>
        )}
      </div>

      <dl className="rounded-lg border px-4 py-3 text-sm">
        <dt className="text-muted-foreground">{t.redirectsTo}</dt>
        <dd className="font-mono font-medium break-all">{client.redirectHost}</dd>
        {trustNote && <dd className="text-xs text-muted-foreground mt-1">{trustNote}</dd>}
      </dl>

      {client.redirectTrust === "unknown" && (
        <Alert variant="warning">
          <AlertTriangle aria-hidden="true" />
          <AlertTitle>{t.unknownHostTitle}</AlertTitle>
          <AlertDescription>{t.unknownHostBody}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
